// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { SalesOffice } from "@gcg/App/Components/SalesOffice/SalesOffice";
import { companyLogoItem, pageStructureStyles } from "@gcg/App/SharedStyles/GeneralStyles";
import { screen } from "@gcg/XDL/Helpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryStringCaseInsensitive from "@insite/client-framework/Common/Utilities/parseQueryStringCaseInsensitive";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getOrderState, OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import loadOrderStatusMappings from "@insite/client-framework/Store/Data/OrderStatusMappings/Handlers/LoadOrderStatusMappings";
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import displayOrder from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/DisplayOrder";
import setVmiOrderDetailsPage from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/SetVmiOrderDetailsPage";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem } from "@insite/mobius/index";
import Page from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);

    let orderNumber;
    let isVmiOrder = false;
    if (location && location.search) {
        const parsedQuery = parseQueryStringCaseInsensitive(location.search, {
            orderNumber: "",
            isVmiOrder: false,
        });

        orderNumber = parsedQuery.orderNumber;
        isVmiOrder = parsedQuery.isVmiOrder;
    }

    const orderState = getOrderState(state, orderNumber);
    const orderStatusMappingDataView = getOrderStatusMappingDataView(state);
    const isOrderNumberStoredInState = state.pages.orderDetails.orderNumber === orderNumber;

    return {
        orderNumber,
        orderState,
        shouldDisplayOrder: !isOrderNumberStoredInState,
        shouldLoadOrderStatusMappings: !orderStatusMappingDataView.value && !orderStatusMappingDataView.isLoading,
        isVmiOrder,
        isVmiOrderDetailsPage: state.pages.orderDetails.isVmiOrderDetailsPage,
    };
};

const mapDispatchToProps = {
    displayOrder,
    loadOrderStatusMappings,
    setVmiOrderDetailsPage,
};

export interface OrderDetailsPageStyles {
    loadFailedWrapper?: InjectableCss;
    loadFailedText?: TypographyPresentationProps;
    titleGridItem?: GridItemProps;
    buttonSetGridItem?: GridItemProps;
    messageRichContentGridItem?: GridItemProps;
    repInfoOrderedByGridItem?: GridItemProps;
    repInfoGridItem?: GridItemProps;
    orderedByGridItem?: GridItemProps;
    dateStatusPOGridItem?: GridItemProps;
    paymentShipOnlineGridItem?: GridItemProps;
    billingInfoGridItem?: GridItemProps;
    shippingInfoGridItem?: GridItemProps;
    shipmentPackagesWrapper?: InjectableCss;
    orderSummaryGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
    orderDetailsInformationGridItem?: GridItemProps;
    printableSalesInfoWrapper?: GridContainerProps;
    orderLines?: GridItemProps;
}

export const orderDetailsPageStyles: OrderDetailsPageStyles = {
    loadFailedWrapper: {
        css: css`
            display: flex;
            height: 200px;
            justify-content: center;
            align-items: center;
            background-color: ${getColor("common.accent")};
        `,
    },
    loadFailedText: {
        weight: "bold",
    },
    titleGridItem: {
        width: [10, 10, 6, 6, 4],
        printWidth: 3,
        css: css`
            align-items: flex-end;
            @media print {
                max-width: 25%;
                flex-basis: 25%;
            }
        `,
    },
    buttonSetGridItem: {
        width: [2, 2, 6, 6, 8],
        printWidth: 9,
        css: css`
            align-items: flex-end;
            @media print {
                max-width: 75%;
                flex-basis: 75%;
            }
        `,
    },
    messageRichContentGridItem: {
        width: 12,
        css: css`
            @media print and (orientation: landscape) {
                padding-top: 10px;
            }
        `,
    },
    repInfoOrderedByGridItem: {
        width: 12,
        css: css`
            @media print {
                display: none !important;
            }
        `,
    },
    repInfoGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    orderedByGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    dateStatusPOGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    paymentShipOnlineGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    billingInfoGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    shippingInfoGridItem: {
        width: [12, 6, 6, 6, 6],
    },
    shipmentPackagesWrapper: {
        css: css`
            width: 100%;
            overflow-x: auto;
            margin-bottom: 30px;

            @media print {
                margin-top: 30px;
                overflow-x: hidden;
                td {
                    white-space: normal;
                }
            }
        `,
    },
    orderSummaryGridItem: {
        width: 12,
    },
    notesGridItem: {
        width: 12,
    },
    orderDetailsInformationGridItem: {
        width: 12,
    },
    printableSalesInfoWrapper: {
        css: css`
            display: none;

            @media print {
                display: block;
            }
        `,
    },
    orderLines: {
        css: css`
            @media screen {
                margin-top: 30px;
            }
        `,
    },
};

export const shipmentPackages: any = {};
export const orderLines: any = {};
export const orderLineShipmentData: any = {};

export let orderShipmentData: any = {};

export const getOrderLineShipmentData = (id: any): any => {
    if (orderLineShipmentData[id]) {
        return orderLineShipmentData[id];
    }

    orderLineShipmentData[id] = [];

    for (const key in shipmentPackages) {
        // eslint-disable-next-line no-prototype-builtins
        if (!shipmentPackages.hasOwnProperty(key)) {
            continue;
        }

        const shipmentPackage = shipmentPackages[key];
        const shipmentPackageData = orderShipmentData[shipmentPackage.id];
        const shipmentPackageProductLines = shipmentPackage?.shipmentPackageLineDtos;

        if (shipmentPackageProductLines && shipmentPackageProductLines.length > 0) {
            for (let i = 0; i < shipmentPackageProductLines.length; i++) {
                const shipmentPackageProductLine = shipmentPackageProductLines[i];

                if (shipmentPackageProductLine.orderLineId === id) {
                    const obj = {} as any;

                    obj.id = shipmentPackageProductLine.id;
                    obj.shipmentDate = shipmentPackage.shipmentDate;
                    obj.trackingUrl = shipmentPackage.trackingUrl;
                    obj.deliveryNumber = shipmentPackageData.DeliveryNumber;
                    obj.qtyShipped = shipmentPackageProductLine.qtyShipped;
                    // XNG-Changes: GCG-695: add tracking number to order line
                    obj.trackingNumber = shipmentPackage.trackingNumber;
                    orderLineShipmentData[id].push(obj);
                }
            }
        }
    }

    return orderLineShipmentData[id];
};

type Props = ResolveThunks<typeof mapDispatchToProps> & PageProps & ReturnType<typeof mapStateToProps>;

class OrderDetailsPage extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        // The share entity (for orders) functionality can use this page
        // to render HTML for PDF generation, so we need to leave this SSR
        // ability so that functionality works.
        if (this.props.shouldLoadOrderStatusMappings) {
            this.props.loadOrderStatusMappings();
        }
        if (this.props.orderNumber && this.props.shouldDisplayOrder) {
            this.props.displayOrder({
                orderNumber: this.props.orderNumber,
                expand: this.props.isVmiOrder ? ["orderLines", "shipments", "vmidetails"] : ["orderLines", "shipments"],
            });
        }
        if (this.props.isVmiOrderDetailsPage) {
            this.props.setVmiOrderDetailsPage(false);
        }
    }

    render() {
        const styles = orderDetailsPageStyles;
        const order = this.props.orderState && this.props.orderState.value;

        if (order && order.orderLines && order.orderLines.length > 0) {
            for (let i = 0; i < order.orderLines.length; i++) {
                const orderLine = order.orderLines[i];

                orderLines[orderLine.id] = orderLine;
            }
        }

        if (order && order.shipmentPackages && order.shipmentPackages.length > 0) {
            for (let i = 0; i < order.shipmentPackages.length; i++) {
                const shipmentPackage = order.shipmentPackages[i];

                shipmentPackages[shipmentPackage.id] = shipmentPackage;
            }
        }

        if (order && order.properties && order.properties.shipmentsData) {
            orderShipmentData = JSON.parse(order.properties.shipmentsData);
        }

        return (
            <Page>
                {this.props.orderState.errorStatusCode === 404 ? (
                    <StyledWrapper {...styles.loadFailedWrapper}>
                        <Typography {...styles.loadFailedText}>{siteMessage("Order_NotFound")}</Typography>
                    </StyledWrapper>
                ) : (
                    <OrderStateContext.Provider value={this.props.orderState}>
                        <GridContainer>
                            <GridItem {...pageStructureStyles.headerGridItem}>
                                <GridContainer>
                                    <GridItem {...styles.titleGridItem}>
                                        <StyledWrapper {...companyLogoItem}>
                                            <img
                                                src={siteMessage("Company_Logo") as any}
                                                alt={translate("Company Logo")}
                                            />
                                        </StyledWrapper>
                                        <Zone contentId={this.props.id} zoneName="TitleWidget" />
                                    </GridItem>
                                    <GridItem {...styles.buttonSetGridItem}>
                                        <Zone contentId={this.props.id} zoneName="ButtonSetWidget" />
                                        <GridContainer {...styles.printableSalesInfoWrapper}>
                                            {order && order.properties && order.properties.salesOffice && (
                                                <GridItem width={5}>
                                                    <SalesOffice
                                                        salesOfficeData={order.properties.salesOffice}
                                                        clickable={true}
                                                    />
                                                </GridItem>
                                            )}
                                            <GridItem width={7}>
                                                <GridContainer>
                                                    <GridItem {...styles.repInfoGridItem}>
                                                        <Zone contentId={this.props.id} zoneName="RepInfoWidget" />
                                                    </GridItem>
                                                    <GridItem {...styles.orderedByGridItem}>
                                                        <Zone contentId={this.props.id} zoneName="OrderedByWidget" />
                                                    </GridItem>
                                                </GridContainer>
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...pageStructureStyles.contentOneGridItem}>
                                <GridContainer>
                                    <GridItem {...styles.messageRichContentGridItem}>
                                        <Zone contentId={this.props.id} zoneName="MessageRichContentWidget" />
                                    </GridItem>
                                    <GridItem {...styles.orderDetailsInformationGridItem}>
                                        <GridContainer>
                                            <GridItem {...styles.dateStatusPOGridItem}>
                                                <Zone contentId={this.props.id} zoneName="DateStatusPOWidgets" />
                                            </GridItem>
                                            <GridItem {...styles.paymentShipOnlineGridItem}>
                                                <Zone contentId={this.props.id} zoneName="PaymentShipOnlineWidgets" />
                                            </GridItem>
                                            <GridItem {...styles.billingInfoGridItem}>
                                                <Zone contentId={this.props.id} zoneName="BillingInfoWidget" />
                                            </GridItem>
                                            <GridItem {...styles.shippingInfoGridItem}>
                                                <Zone contentId={this.props.id} zoneName="ShippingInfoWidget" />
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...pageStructureStyles.contentTwoGridItem}>
                                <GridContainer>
                                    <GridItem {...styles.repInfoOrderedByGridItem}>
                                        <GridContainer>
                                            <GridItem {...styles.repInfoGridItem}>
                                                <Zone contentId={this.props.id} zoneName="RepInfoWidget" />
                                            </GridItem>
                                            <GridItem {...styles.orderedByGridItem}>
                                                <Zone contentId={this.props.id} zoneName="OrderedByWidget" />
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                    <GridItem {...styles.orderSummaryGridItem}>
                                        <Zone contentId={this.props.id} zoneName="OrderSummaryWidget" />
                                    </GridItem>
                                    <GridItem {...styles.notesGridItem}>
                                        <Zone contentId={this.props.id} zoneName="NotesWidget" />
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        </GridContainer>
                        <StyledWrapper {...styles.orderLines}>
                            <StyledWrapper {...styles.shipmentPackagesWrapper}>
                                <Zone contentId={this.props.id} zoneName="ShipmentPackagesWidget" />
                            </StyledWrapper>
                            <Zone contentId={this.props.id} zoneName="OrderLinesWidget" />
                        </StyledWrapper>
                    </OrderStateContext.Provider>
                )}
                <Modals />
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderDetailsPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "OrderDetailsPage" instead of this constant.
 */
export const OrderDetailsPageContext = "OrderDetailsPage";
